/**
 * LineageTimelineStrip — horizontal bottom lineage timeline (shared).
 *
 * Sprint-237 (FE-1 S2, sdd.md §4.2 / §4.2.1): the design's signature
 * bottom temporal strip, mounted on BOTH surfaces:
 *   - /environment (ambient — global-container runs; honest-empty when none)
 *   - /theatres/:id/build (scoped — theatre_id-filtered runs)
 *   - /workspace (cross-theatre, newest-first, limit 50)
 *
 * Composition (extract-don't-mutate): LineageTrack is the track extracted
 * from LineageScrubber; status colors come from RunTimeline's canonical
 * STATUS_COLORS map (imported by LineageTrack — never forked). RunTimeline
 * itself keeps its vertical form untouched (live consumers: OperatorPanel,
 * EnvironmentMonitorPage's local variant — sdd.md §2.5).
 *
 * Data: `useEnvironmentRuns(filters, { demoFallback: false })` — the strip
 * is honest-data-only: a runs-query failure (401 unauthenticated ambient
 * viewer, 500, network) degrades to a small "Failed to load runs" line and
 * NEVER fixtures, NEVER a spinner-hang (deterministic errors fail fast),
 * NEVER blocks the canvas above it.
 *
 * Selection→canvas time-travel is explicitly OUT OF SCOPE (sdd.md §4.2.2):
 * the strip selects/inspects via the active-point chip but does not rewind
 * the graph.
 *
 * Token-clean: CSS vars only (born light-mode-ready, sdd.md §4.5).
 */

import { useLineageTimeline } from '../../hooks/useLineageTimeline';
import { useEnvironmentRuns } from '../../hooks/useEnvironmentRuns';
import type { EnvironmentRunFilters } from '../../types/environmentRun';
import { LineageTrack } from './LineageTrack';

interface LineageTimelineStripProps {
  /** Run-list filters for this mount (theatre_id-scoped or cross-theatre). */
  filters?: EnvironmentRunFilters;
  /** Gate the runs query (e.g. ambient surface before the global container
   * resolves). Disabled ⇒ honest-empty, no fetch. */
  enabled?: boolean;
}

function formatAge(ts: number): string {
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export function LineageTimelineStrip({ filters = {}, enabled = true }: LineageTimelineStripProps) {
  const { runs, isLoading, error } = useEnvironmentRuns(filters, {
    enabled,
    demoFallback: false,
  });
  const timeline = useLineageTimeline(runs);
  const { points, selectedIndex, position, clearSelection } = timeline;

  const activePoint = points.length > 0 ? points[selectedIndex ?? 0] : null;
  const hasError = !!error;

  return (
    <footer
      data-testid="lineage-timeline-strip"
      aria-label="Lineage timeline"
      className="h-16 w-full flex-shrink-0 flex items-center gap-4 px-4 border-t border-[var(--e-border-secondary)] bg-[var(--e-bg-card)]"
    >
      {/* Lane header */}
      <div className="flex flex-col gap-0.5 flex-shrink-0 w-[92px]">
        <span className="text-[9px] font-mono uppercase tracking-widest text-[var(--e-text-muted)]">
          LINEAGE
        </span>
        <span
          data-testid="lineage-strip-position"
          className={`text-[9px] font-mono uppercase tracking-wider ${
            position === 'live'
              ? 'text-[var(--e-green-600)]'
              : 'text-[var(--e-purple-500)]'
          }`}
        >
          {position === 'live' ? 'LIVE' : 'HISTORICAL'}
        </span>
      </div>

      {/* Scrub track / honest states */}
      <div className="flex-1 min-w-0">
        {hasError ? (
          <div
            data-testid="lineage-strip-error"
            role="status"
            className="text-[10px] font-mono text-[var(--e-text-muted)]"
          >
            Failed to load runs
          </div>
        ) : points.length > 0 ? (
          <LineageTrack timeline={timeline} />
        ) : isLoading ? (
          /* Quiet while loading — never a spinner-hang, never blocking. */
          <div className="h-8" aria-hidden="true" />
        ) : (
          <div
            data-testid="lineage-strip-empty"
            role="status"
            className="text-[10px] font-mono text-[var(--e-text-muted)]"
          >
            No runs yet. Execute a run to see the timeline.
          </div>
        )}
      </div>

      {/* Active-point chip */}
      {activePoint && !hasError && (
        <div
          data-testid="lineage-strip-chip"
          className="flex items-center gap-2 flex-shrink-0 rounded border border-[var(--e-border-secondary)] bg-[var(--e-bg-sunken)] px-2.5 py-1.5"
        >
          <span className="text-[10px] font-mono text-[var(--e-text-primary)]">
            Run {activePoint.runId.slice(0, 8)}
          </span>
          <span className="text-[9px] font-mono text-[var(--e-text-muted)]">
            {formatAge(activePoint.timestamp)}
          </span>
          {activePoint.compositeScore != null && (
            <span className="text-[9px] font-mono text-[var(--e-text-secondary)]">
              {activePoint.compositeScore.toFixed(2)}
            </span>
          )}
          {activePoint.brierScore != null && (
            <span className="text-[9px] font-mono text-[var(--e-text-muted)]">
              B:{activePoint.brierScore.toFixed(3)}
            </span>
          )}
          {position === 'historical' && (
            <button
              type="button"
              onClick={clearSelection}
              className="text-[9px] font-mono text-[var(--e-text-muted)] hover:text-[var(--e-text-primary)] transition-colors"
            >
              Return to live →
            </button>
          )}
        </div>
      )}
    </footer>
  );
}
